import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { canShowScene, loadScene } from "./sceneLoader";
import LoginMapFallback from "./LoginMapFallback";

/**
 * The far side of the login page: the landing page's 3D town, held on its
 * "07:30, at the office" shot while the morning run plays on a loop.
 *
 * The drawn map shows first and stays wherever the 3D cannot run
 * (`canShowScene`), or fails to load; the town fades in over it once built.
 * The scene starts only after the form is up, never delays it, pauses with the
 * tab (requestAnimationFrame does), holds still once nobody has touched the
 * page for a while, and gives its WebGL context back when the page unmounts —
 * which a successful sign-in does.
 */

// The morning run, 07:06 → 07:44: three of the four buses on the road.
const FROM = 7 * 60 + 6;
const TO = 7 * 60 + 44;
const MINUTES_PER_SECOND = 0.3;
const BUSES = [1, 2, 3, 4];
// No pointer or key for this long — an office screen left on the login page —
// and the town holds its last frame: no GPU at all until someone is back.
const IDLE_MS = 30000;
const INPUT_EVENTS = ["pointermove", "pointerdown", "keydown", "touchstart"];

function disposeWorld(world) {
  world.ready = false;
  if (world.scene) {
    world.scene.traverse((o) => {
      if (o.geometry) o.geometry.dispose();
      const mats = Array.isArray(o.material) ? o.material : o.material ? [o.material] : [];
      mats.forEach((m) => {
        Object.keys(m).forEach((k) => {
          if (m[k] && m[k].isTexture) m[k].dispose();
        });
        m.dispose();
      });
    });
  }
  if (world.renderer) {
    world.renderer.dispose();
    world.renderer.forceContextLoss();
  }
  world.renderer = null;
  world.scene = null;
}

function LoginScene() {
  const { t, i18n } = useTranslation();
  const [live, setLive] = useState(false);
  const hostRef = useRef(null);
  const canvasRef = useRef(null);
  const dimRef = useRef(null);
  const pillRefs = useRef([]);
  const pillState = useRef(BUSES.map(() => ({ status: null, on: false })));
  const worldRef = useRef(null);
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (!canShowScene()) return undefined;
    let cancelled = false;
    let raf = 0;
    let observer = null;
    let world = null;
    let inputAt = performance.now();
    const onInput = () => {
      inputAt = performance.now();
    };
    const point = {};

    const resize = () => {
      const host = hostRef.current;
      if (!host || !world) return;
      const { width, height } = host.getBoundingClientRect();
      if (width > 0 && height > 0) world.resize(Math.round(width), Math.round(height));
    };

    // The landing page's office labels: the buses on the road, and Bus 4.
    const placePills = () => {
      const anchors = world.anchors();
      BUSES.forEach((n, i) => {
        const el = pillRefs.current[i];
        const bus = world.buses[n - 1];
        const st = pillState.current[i];
        if (!el || !bus) return;
        let on = bus.status === "way" || n === 4;
        if (on) {
          world.project(anchors[`bus${n}`], point);
          on = point.vis && point.x > 30 && point.x < world.vw - 30 && point.y > 70 && point.y < world.vh - 10;
        }
        if (on) {
          el.style.transform = `translate(${point.x.toFixed(1)}px, ${point.y.toFixed(1)}px) translate(-50%, -100%)`;
          if (st.status !== bus.status) {
            st.status = bus.status;
            el.dataset.status = bus.status;
            el.querySelector("small").textContent = tRef.current(`auth.login.scene.status.${bus.status}`);
          }
        }
        if (on !== st.on) {
          el.classList.toggle("is-on", on);
          st.on = on;
        }
      });
    };

    const start = async () => {
      const WOS = await loadScene();
      if (cancelled) return;
      WOS.bootWorld();
      world = worldRef.current = WOS.world;
      world.ready = false;
      const S = world.state;
      Object.assign(S, {
        minute: FROM,
        shotA: "office",
        shotAP: 0,
        shotAPx: undefined,
        shotAH: undefined,
        shotB: null,
        blend: 1,
        grid: 1,
        fleet: 1,
        routes: 0,
        live: true,
        liveT: performance.now() / 1000,
        phone: false,
        reduced: false,
        colOff: 0,
      });
      // 30 frames a second: a backdrop drifting behind a form needs no more,
      // and a typing hand should not share the machine with the town at 60.
      await world.init(canvasRef.current, {
        busName: tRef.current("auth.login.scene.busBanner"),
        fps: 30,
      });
      if (cancelled) return;
      resize();
      observer = new ResizeObserver(resize);
      observer.observe(hostRef.current);
      setLive(true);
      INPUT_EVENTS.forEach((ev) => window.addEventListener(ev, onInput, { passive: true }));

      let last = 0;
      let looping = false;
      const frame = (now) => {
        raf = requestAnimationFrame(frame);
        if (now - inputAt > IDLE_MS) return;
        // world.q.frameMs: the 30 above, or slower once the town has stepped down
        if (last && now - last < world.q.frameMs - 3) return;
        const dt = last ? Math.min(0.1, (now - last) / 1000) : 0.016;
        last = now;
        S.minute += dt * MINUTES_PER_SECOND;
        // a slow drift along the office shot, as when the landing page is read
        S.shotAP = 0.5 + 0.5 * Math.sin(now / 25000);
        if (S.minute >= TO && !looping) {
          looping = true;
          dimRef.current?.classList.add("is-on");
          setTimeout(() => {
            if (cancelled) return;
            S.minute = FROM;
            world.snapCamera();
            dimRef.current?.classList.remove("is-on");
            looping = false;
          }, 800);
        }
        world.frame(now);
        placePills();
      };
      raf = requestAnimationFrame(frame);
    };

    const begin = () => {
      start().catch((e) => {
        // The drawn map stays.
        if (window.console) console.warn("Login scene unavailable", e);
      });
    };
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(begin, { timeout: 1500 })
      : window.setTimeout(begin, 400);

    return () => {
      cancelled = true;
      if (window.requestIdleCallback) window.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
      cancelAnimationFrame(raf);
      INPUT_EVENTS.forEach((ev) => window.removeEventListener(ev, onInput));
      if (observer) observer.disconnect();
      if (world) disposeWorld(world);
      worldRef.current = null;
    };
  }, []);

  // A language switch: the bus banners, and the status lines on the next frame.
  useEffect(() => {
    const world = worldRef.current;
    if (world && world.ready) {
      const name = t("auth.login.scene.busBanner");
      world.setBusName(name, /[֐-ࣿ]/.test(name));
    }
    pillState.current.forEach((st) => {
      st.status = null;
    });
  }, [i18n.language, t]);

  return (
    <div className="wl-stage" ref={hostRef} aria-hidden="true">
      <LoginMapFallback className={`wl-map${live ? " is-off" : ""}`} />
      <canvas ref={canvasRef} className={`wl-gl${live ? " is-on" : ""}`} />
      <div className="wl-pills">
        {BUSES.map((n, i) => (
          <div
            key={n}
            className="wl-pill"
            ref={(el) => {
              pillRefs.current[i] = el;
            }}
          >
            <span className="wl-pill-dot" />
            {t("auth.login.scene.bus", { n })}
            <small />
          </div>
        ))}
      </div>
      <div className="wl-dim" ref={dimRef} />
      <div className="wl-fade" />
      <div className="wl-caption">
        <p className="wl-caption-title">{t("auth.login.scene.title")}</p>
      </div>
    </div>
  );
}

export default LoginScene;
